import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRight } from "lucide-react";
import { api, apiErrorCode } from "@/lib/api";
import { useAuth, type CurrentUser } from "@/lib/auth";

const schema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Requerido"),
});

type Form = z.infer<typeof schema>;

export default function LoginPage() {
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  async function onSubmit(values: Form) {
    setServerError(null);
    try {
      const { data } = await api.post<{ access_token: string; user: CurrentUser }>(
        "/auth/login",
        { email: values.email.toLowerCase(), password: values.password }
      );
      setSession(data.access_token, data.user);
      nav("/overview", { replace: true });
    } catch (err) {
      const code = apiErrorCode(err);
      setServerError(
        code === "INVALID_CREDENTIALS" ? "Email o contraseña incorrectos" : "No se pudo iniciar sesión"
      );
    }
  }

  return (
    <div className="min-h-[100dvh] bg-white lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      {/* PANEL IZQUIERDO — editorial oscuro */}
      <aside className="relative hidden lg:flex flex-col bg-ink text-white overflow-hidden">
        {/* Halo plateado sutil, esquina inferior izquierda */}
        <div
          aria-hidden
          className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full blur-[120px] opacity-[0.18]"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--silver-hi)) 0%, transparent 65%)",
          }}
        />
        {/* Trama de líneas verticales muy sutiles */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 96px)",
          }}
        />

        {/* Marca superior */}
        <header className="relative z-10 flex items-center justify-between p-10">
          <img
            src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
            alt="NiuFit Partners"
            className="h-8 w-auto animate-fade-up"
          />
          <LiveClock />
        </header>

        {/* Bloque editorial central */}
        <section className="relative z-10 flex-1 flex flex-col justify-center px-10 xl:px-14">
          <div className="max-w-2xl">
            <div
              className="font-mono-num text-[11px] uppercase tracking-[0.22em] text-white/60 animate-fade-up"
              style={{ animationDelay: "80ms" }}
            >
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-silver animate-pulse-dot" />
                Panel Partners — NiuFit
              </span>
            </div>

            <h1
              className="font-display mt-8 text-6xl xl:text-7xl font-medium leading-[0.98] animate-fade-up"
              style={{ animationDelay: "160ms" }}
            >
              Ofrece más<br />
              a tus socios.<br />
              <em className="not-italic text-silver-metal font-medium italic">
                Gana
              </em>{" "}
              con cada uno.
            </h1>

            <p
              className="mt-8 text-white/55 text-lg max-w-lg leading-relaxed animate-fade-up"
              style={{ animationDelay: "240ms" }}
            >
              Suma coach IA, nutrición y seguimiento inteligente al servicio
              de tu gimnasio. NiuFit entrega el producto —{" "}
              <span className="text-white/80">tú recibes una comisión</span>{" "}
              por cada suscripción activada.
            </p>

          </div>
        </section>

        {/* Pie silencioso */}
        <footer className="relative z-10 px-10 xl:px-14 pb-10 pt-6 space-y-6">
          <CapabilitiesTicker
            items={[
              "Ingresos recurrentes",
              "Renovación automática",
              "Códigos al instante",
              "Reportes en vivo",
            ]}
          />
          <div className="flex items-end justify-between text-xs font-mono-num text-white/40 uppercase tracking-wider">
            <span>© {new Date().getFullYear()} NiuFit</span>
            <span>Lima — Perú</span>
          </div>
        </footer>
      </aside>

      {/* PANEL DERECHO — form */}
      <main className="flex items-center justify-center px-6 py-12 lg:px-12 xl:px-16">
        <div className="w-full max-w-sm animate-fade-up">
          {/* Logo mobile — invertido a oscuro sobre blanco */}
          <div className="lg:hidden mb-14">
            <img
              src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
              alt="NiuFit Partners"
              className="h-8 w-auto"
              style={{ filter: "invert(1) brightness(0.05)" }}
            />
          </div>

          <div className="mb-10">
            <h2 className="font-display text-4xl font-medium tracking-tight leading-none">
              Ingresar.
            </h2>
            <p className="text-sm text-muted-foreground mt-4">
              Continúa con la cuenta de tu partner.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FieldGroup label="Email" error={errors.email?.message} touched={!!touchedFields.email}>
              <input
                type="email"
                autoComplete="email"
                placeholder="tu@partner.com"
                className="w-full bg-transparent border-b border-border pb-2.5 pt-2 text-[15px] outline-none focus:border-ink transition-colors placeholder:text-muted-foreground/50"
                {...register("email")}
              />
            </FieldGroup>

            <FieldGroup label="Contraseña" error={errors.password?.message} touched={!!touchedFields.password}>
              <input
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full bg-transparent border-b border-border pb-2.5 pt-2 text-[15px] outline-none focus:border-ink transition-colors placeholder:text-muted-foreground/50"
                {...register("password")}
              />
            </FieldGroup>

            <div className="text-right -mt-3">
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-ink transition-colors underline-offset-4 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {serverError && (
              <div className="rounded border border-destructive/25 bg-destructive/[0.03] px-3 py-2 text-sm text-destructive">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full h-14 rounded-md bg-ink text-white font-semibold overflow-hidden transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:shadow-[0_16px_32px_-12px_rgba(0,0,0,0.35),_0_0_0_1px_rgba(193,199,206,0.35)] active:translate-y-[1px] disabled:opacity-80 disabled:cursor-wait"
            >
              {/* Sheen plateado que barre suavemente al hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:translate-x-[380%]"
              />
              <span className="relative flex items-center justify-between px-6">
                <span className="text-[15px] tracking-tight inline-flex items-baseline gap-0.5">
                  {isSubmitting ? "Ingresando" : "Ingresar"}
                  {isSubmitting && (
                    <span aria-hidden className="inline-flex gap-0.5 ml-0.5">
                      <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "0ms" }} />
                      <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "200ms" }} />
                      <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "400ms" }} />
                    </span>
                  )}
                </span>
                <ArrowUpRight
                  className={[
                    "h-4 w-4 text-silver-hi transition-all duration-300",
                    isSubmitting ? "opacity-40" : "group-hover:translate-x-1 group-hover:-translate-y-1",
                  ].join(" ")}
                  strokeWidth={2.25}
                />
              </span>
              {/* Barra plateada progresiva durante loading */}
              {isSubmitting && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden"
                >
                  <span className="absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-silver-hi to-transparent animate-slide-progress" />
                </span>
              )}
            </button>
          </form>

          <div className="mt-14 pt-6 border-t border-border/60 text-xs text-muted-foreground flex items-center justify-between">
            <span>¿Nuevo partner?</span>
            <a
              href="mailto:partners@niufit.app"
              className="text-ink font-medium hover:underline underline-offset-4 decoration-2 decoration-neutral-400"
            >
              Habla con NiuFit →
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ─────────────── Componentes de composición ─────────────── */

function FieldGroup({
  label,
  error,
  touched: _touched,
  children,
}: {
  label: string;
  error?: string;
  touched: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-destructive pt-1">{error}</p>}
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <div className="text-right animate-fade-up" style={{ animationDelay: "40ms" }}>
      <div className="font-mono-num text-white/80 text-[13px] tabular-nums">
        {hh}:{mm}
        <span className="text-white/30">:{ss}</span>
      </div>
      <div className="text-[10px] font-mono-num uppercase tracking-[0.18em] text-white/30 mt-0.5">
        Local · Ahora
      </div>
    </div>
  );
}

function CapabilitiesTicker({ items, interval = 2800 }: { items: string[]; interval?: number }) {
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLSpanElement | null>>([]);

  // Rotación
  useEffect(() => {
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % items.length);
    }, interval);
    return () => window.clearInterval(id);
  }, [items.length, interval]);

  // Reposiciona el indicador cuando cambia el activo o el layout
  useEffect(() => {
    const update = () => {
      const el = itemRefs.current[active];
      const cont = containerRef.current;
      if (!el || !cont) return;
      const c = cont.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      setPos({ left: r.left - c.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    // Font swap tardío puede desplazar posiciones
    const t = window.setTimeout(update, 300);
    return () => {
      window.removeEventListener("resize", update);
      window.clearTimeout(t);
    };
  }, [active, items.length]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex flex-wrap items-center gap-x-3 gap-y-1 pb-2 text-[11px] font-mono-num uppercase tracking-[0.14em] text-white/45"
    >
      {items.map((item, i) => (
        <span key={item} className="inline-flex items-center gap-3">
          {i > 0 && <span className="text-white/15">/</span>}
          <span ref={(el) => { itemRefs.current[i] = el; }}>{item}</span>
        </span>
      ))}
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0 h-px rounded-full bg-silver-metal transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]"
        style={{
          left: pos?.left ?? 0,
          width: pos?.width ?? 0,
          opacity: pos ? 1 : 0,
        }}
      />
    </div>
  );
}
